import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";
import {
  ArrowLeft, Download, FileSpreadsheet, FileText, Search, Users,
  DollarSign, Clock, RefreshCw, Filter, Calendar, X, File, FileType, FileJson, BookOpen, Building,
  BarChart3, TrendingUp, AlertTriangle, Shield, Printer, Check, ChevronsUpDown
} from "lucide-react";
import { useAuth } from "../../App";
import { toast } from "sonner";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import { applyPlugin } from "jspdf-autotable";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { fmtBs, COLORS } from "./reportesUtils";

applyPlugin(jsPDF);

const MorosidadAE = () => {
  const navigate = useNavigate();
  const { API, token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [loading, setLoading] = useState(false);
  const [reporteData, setReporteData] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filtroMesesMin, setFiltroMesesMin] = useState("");
  const [filtroMesesMax, setFiltroMesesMax] = useState("");
  const [filtroFechaCorte, setFiltroFechaCorte] = useState("");
  const [mostrarFiltros, setMostrarFiltros] = useState(false);

  useEffect(() => { fetchMorosidadData(); }, []);

  const fetchMorosidadData = async (aplicarFiltros = false) => {
    setLoading(true);
    try {
      let url = `${API}/reportes/morosidad-actividades-economicas`;
      const params = new URLSearchParams();
      
      if (aplicarFiltros) {
        if (filtroMesesMin && filtroMesesMin !== "todos") params.append('meses_mora_min', filtroMesesMin);
        if (filtroMesesMax && filtroMesesMax !== "todos") params.append('meses_mora_max', filtroMesesMax);
        if (filtroFechaCorte) params.append('fecha_corte', filtroFechaCorte);
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await axios.get(url, { headers });
      setReporteData(response.data);
      
      if (aplicarFiltros && params.toString()) {
        toast.success("Filtros aplicados correctamente");
      }
    } catch (error) {
      console.error("Error al cargar reporte:", error);
      toast.error("Error al cargar el reporte de morosidad");
    } finally {
      setLoading(false);
    }
  };

  const limpiarFiltros = () => {
    setFiltroMesesMin("");
    setFiltroMesesMax("");
    setFiltroFechaCorte("");
    fetchMorosidadData(false);
    toast.info("Filtros eliminados");
  };

  const hayFiltrosActivos = (filtroMesesMin && filtroMesesMin !== "todos") || 
                            (filtroMesesMax && filtroMesesMax !== "todos") || 
                            filtroFechaCorte;

  // Filtrar contribuyentes por búsqueda
  const filteredContribuyentes = reporteData?.contribuyentes?.filter(c => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      c.rif?.toLowerCase().includes(search) ||
      c.nombre?.toLowerCase().includes(search) ||
      c.direccion?.toLowerCase().includes(search)
    );
  }) || [];

  // Exportar a Excel
  const exportToExcel = () => {
    if (!reporteData) return;
    
    const wsData = [
      ["REPORTE DE MOROSIDAD - ACTIVIDADES ECONÓMICAS"],
      [`Fecha de generación: ${new Date(reporteData.fecha_generacion).toLocaleString('es-VE')}`],
      [],
      ["RESUMEN"],
      ["Total Contribuyentes con LAE", reporteData.total_contribuyentes],
      ["Total Morosos", reporteData.total_morosos],
      ["Porcentaje de Morosidad", `${reporteData.porcentaje_morosidad}%`],
      ["Deuda Estimada Total", `Bs. ${reporteData.total_deuda_estimada.toLocaleString('es-VE')}`],
      [],
      ["DETALLE DE CONTRIBUYENTES MOROSOS"],
      ["RIF", "Nombre/Razón Social", "Última Declaración", "Meses de Mora", "Deuda Estimada", "Dirección", "Teléfono", "Email"]
    ];
    
    reporteData.contribuyentes.forEach(c => {
      wsData.push([
        c.rif,
        c.nombre,
        c.ultima_declaracion,
        c.meses_mora,
        c.deuda_estimada,
        c.direccion,
        c.telefono,
        c.email
      ]);
    });

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    
    // Ajustar anchos de columna
    ws['!cols'] = [
      { wch: 15 }, { wch: 35 }, { wch: 18 }, { wch: 15 }, 
      { wch: 15 }, { wch: 30 }, { wch: 15 }, { wch: 25 }
    ];
    
    XLSX.utils.book_append_sheet(wb, ws, "Morosidad");
    XLSX.writeFile(wb, `Reporte_Morosidad_AE_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success("Reporte exportado a Excel exitosamente");
  };

  // Exportar a PDF
  const exportToPDF = () => {
    if (!reporteData) return;
    
    const doc = new jsPDF('l', 'mm', 'a4'); // Landscape
    
    // Título
    doc.setFontSize(18);
    doc.setTextColor(30, 64, 175);
    doc.text("REPORTE DE MOROSIDAD - ACTIVIDADES ECONÓMICAS", 14, 20);
    
    // Fecha de generación
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Fecha de generación: ${new Date(reporteData.fecha_generacion).toLocaleString('es-VE')}`, 14, 28);
    
    // Resumen
    doc.setFontSize(12);
    doc.setTextColor(0);
    doc.text("RESUMEN", 14, 40);
    
    doc.setFontSize(10);
    doc.text(`Total Contribuyentes con LAE: ${reporteData.total_contribuyentes}`, 14, 48);
    doc.text(`Total Morosos: ${reporteData.total_morosos}`, 14, 54);
    doc.text(`Porcentaje de Morosidad: ${reporteData.porcentaje_morosidad}%`, 14, 60);
    doc.text(`Deuda Estimada Total: Bs. ${reporteData.total_deuda_estimada.toLocaleString('es-VE')}`, 14, 66);
    
    // Tabla de contribuyentes
    const tableData = reporteData.contribuyentes.map(c => [
      c.rif,
      c.nombre?.substring(0, 30) || '',
      c.ultima_declaracion,
      c.meses_mora.toString(),
      `Bs. ${c.deuda_estimada.toLocaleString('es-VE')}`,
      c.telefono || '-'
    ]);
    
    doc.autoTable({
      startY: 75,
      head: [['RIF', 'Nombre/Razón Social', 'Última Declaración', 'Meses Mora', 'Deuda Estimada', 'Teléfono']],
      body: tableData,
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [30, 64, 175], textColor: 255 },
      alternateRowStyles: { fillColor: [245, 247, 250] },
      columnStyles: {
        0: { cellWidth: 30 },
        1: { cellWidth: 60 },
        2: { cellWidth: 30 },
        3: { cellWidth: 25 },
        4: { cellWidth: 35 },
        5: { cellWidth: 30 }
      }
    });
    
    // Pie de página
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(128);
      doc.text(
        `SIRET - Sistema de Recaudación Tributaria Municipal | Página ${i} de ${pageCount}`,
        doc.internal.pageSize.getWidth() / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: 'center' }
      );
    }
    
    doc.save(`Reporte_Morosidad_AE_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success("Reporte exportado a PDF exitosamente");
  };

  return (
    <div className="space-y-6" data-testid="reporte-morosidad">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/reportes")}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
            <AlertTriangle className="w-6 h-6 text-red-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Morosidad - Actividades Económicas
            </h1>
            <p className="text-slate-500">Contribuyentes con LAE con meses pendientes de declarar</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button 
            variant={mostrarFiltros ? "default" : "outline"} 
            onClick={() => setMostrarFiltros(!mostrarFiltros)}
            className={mostrarFiltros ? "bg-blue-600 hover:bg-blue-700" : ""}
          >
            <Filter className="w-4 h-4 mr-2" />
            Filtros
            {hayFiltrosActivos && (
              <span className="ml-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {[
                  filtroMesesMin && filtroMesesMin !== "todos" ? filtroMesesMin : null, 
                  filtroMesesMax && filtroMesesMax !== "todos" ? filtroMesesMax : null, 
                  filtroFechaCorte
                ].filter(Boolean).length}
              </span>
            )}
          </Button>
          <Button variant="outline" onClick={() => fetchMorosidadData(false)} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
          <Button variant="outline" onClick={exportToExcel} disabled={!reporteData}>
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Excel
          </Button>
          <Button variant="outline" onClick={exportToPDF} disabled={!reporteData}>
            <FileText className="w-4 h-4 mr-2" />
            PDF
          </Button>
        </div>
      </div>

      {/* Panel de Filtros */}
      {mostrarFiltros && (
        <Card className="border-blue-200 bg-blue-50/50">
          <CardContent className="p-4">
            <div className="flex items-end gap-4 flex-wrap">
              <div className="flex-1 min-w-[200px]">
                <Label className="text-sm font-medium text-slate-700 mb-1.5 block">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  Fecha de Corte
                </Label>
                <Input
                  type="month"
                  value={filtroFechaCorte}
                  onChange={(e) => setFiltroFechaCorte(e.target.value)}
                  className="bg-white"
                  placeholder="YYYY-MM"
                  data-testid="filtro-fecha-corte"
                />
                <p className="text-xs text-slate-500 mt-1">Calcula morosidad a esta fecha</p>
              </div>
              
              <div className="w-[140px]">
                <Label className="text-sm font-medium text-slate-700 mb-1.5 block">
                  Meses Mora (Mín)
                </Label>
                <Select value={filtroMesesMin} onValueChange={setFiltroMesesMin}>
                  <SelectTrigger className="bg-white" data-testid="filtro-meses-min">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos</SelectItem>
                    <SelectItem value="1">1+ meses</SelectItem>
                    <SelectItem value="3">3+ meses</SelectItem>
                    <SelectItem value="6">6+ meses</SelectItem>
                    <SelectItem value="12">12+ meses</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="w-[140px]">
                <Label className="text-sm font-medium text-slate-700 mb-1.5 block">
                  Meses Mora (Máx)
                </Label>
                <Select value={filtroMesesMax} onValueChange={setFiltroMesesMax}>
                  <SelectTrigger className="bg-white" data-testid="filtro-meses-max">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos</SelectItem>
                    <SelectItem value="3">Hasta 3</SelectItem>
                    <SelectItem value="6">Hasta 6</SelectItem>
                    <SelectItem value="12">Hasta 12</SelectItem>
                    <SelectItem value="24">Hasta 24</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex gap-2">
                <Button 
                  onClick={() => fetchMorosidadData(true)} 
                  disabled={loading}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Search className="w-4 h-4 mr-2" />
                  Aplicar
                </Button>
                {hayFiltrosActivos && (
                  <Button variant="outline" onClick={limpiarFiltros} disabled={loading}>
                    <X className="w-4 h-4 mr-2" />
                    Limpiar
                  </Button>
                )}
              </div>
            </div>
            
            {reporteData?.filtros_aplicados && (
              <div className="mt-3 pt-3 border-t border-blue-200">
                <p className="text-xs text-slate-600">
                  <strong>Fecha de corte:</strong> {reporteData.fecha_corte || "Actual"} | 
                  <strong className="ml-2">Filtros:</strong> {
                    reporteData.filtros_aplicados.meses_mora_min || reporteData.filtros_aplicados.meses_mora_max 
                      ? `${reporteData.filtros_aplicados.meses_mora_min || 1} - ${reporteData.filtros_aplicados.meses_mora_max || '∞'} meses`
                      : "Ninguno"
                  }
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      ) : reporteData ? (
        <>
          {/* Tarjetas de resumen */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Total Contribuyentes</p>
                    <p className="text-3xl font-bold text-slate-900">{reporteData.total_contribuyentes}</p>
                  </div>
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Users className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Total Morosos</p>
                    <p className="text-3xl font-bold text-red-600">{reporteData.total_morosos}</p>
                  </div>
                  <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                    <AlertTriangle className="w-6 h-6 text-red-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">% Morosidad</p>
                    <p className="text-3xl font-bold text-orange-600">{reporteData.porcentaje_morosidad}%</p>
                  </div>
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-orange-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Deuda Estimada</p>
                    <p className="text-2xl font-bold text-green-600">Bs. {reporteData.total_deuda_estimada.toLocaleString('es-VE')}</p>
                  </div>
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Gráficos */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Gráfico de barras */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Distribución por Rango de Meses</CardTitle>
                <CardDescription>Cantidad de contribuyentes morosos por tiempo de mora</CardDescription>
              </CardHeader>
              <CardContent>
                {reporteData.resumen_por_meses.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={reporteData.resumen_por_meses}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="rango" tick={{ fontSize: 12 }} />
                      <YAxis />
                      <Tooltip 
                        formatter={(value, name) => [value, name === 'cantidad' ? 'Contribuyentes' : 'Deuda Estimada']}
                      />
                      <Legend />
                      <Bar dataKey="cantidad" name="Contribuyentes" fill="#EF4444" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-64 text-slate-400">
                    No hay datos de morosidad para mostrar
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Gráfico circular */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Distribución de Deuda</CardTitle>
                <CardDescription>Deuda estimada por rango de tiempo</CardDescription>
              </CardHeader>
              <CardContent>
                {reporteData.resumen_por_meses.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={reporteData.resumen_por_meses}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ rango, percent }) => `${rango}: ${(percent * 100).toFixed(0)}%`}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="deuda"
                      >
                        {reporteData.resumen_por_meses.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => `Bs. ${value.toLocaleString('es-VE')}`} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-64 text-slate-400">
                    No hay datos de deuda para mostrar
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Tabla de contribuyentes */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Detalle de Contribuyentes</CardTitle>
                  <CardDescription>Lista completa de contribuyentes con morosidad</CardDescription>
                </div>
                <div className="relative w-64">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                  <Input
                    placeholder="Buscar por RIF o nombre..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase">RIF</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase">Nombre/Razón Social</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase">Última Declaración</th>
                      <th className="text-center py-3 px-4 text-xs font-semibold text-slate-600 uppercase">Meses Mora</th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-slate-600 uppercase">Deuda Estimada</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase">Teléfono</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredContribuyentes.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center py-8 text-slate-400">
                          No se encontraron contribuyentes
                        </td>
                      </tr>
                    ) : (
                      filteredContribuyentes.map((contrib, index) => (
                        <tr key={contrib.id || index} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-3 px-4 text-sm font-medium text-slate-900">{contrib.rif}</td>
                          <td className="py-3 px-4 text-sm text-slate-700">{contrib.nombre}</td>
                          <td className="py-3 px-4 text-sm text-slate-600">{contrib.ultima_declaracion}</td>
                          <td className="py-3 px-4 text-center">
                            {contrib.meses_mora > 0 ? (
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                contrib.meses_mora > 12 ? 'bg-red-100 text-red-800' :
                                contrib.meses_mora > 6 ? 'bg-orange-100 text-orange-800' :
                                contrib.meses_mora > 3 ? 'bg-yellow-100 text-yellow-800' :
                                'bg-blue-100 text-blue-800'
                              }`}>
                                {contrib.meses_mora} meses
                              </span>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-sm text-right font-medium text-slate-900">
                            Bs. {contrib.deuda_estimada.toLocaleString('es-VE')}
                          </td>
                          <td className="py-3 px-4 text-sm text-slate-600">{contrib.telefono || '-'}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 text-sm text-slate-500">
                Mostrando {filteredContribuyentes.length} de {reporteData.contribuyentes.length} contribuyentes
              </div>
            </CardContent>
          </Card>
        </>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <AlertTriangle className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No se pudieron cargar los datos del reporte</p>
            <Button variant="outline" onClick={fetchMorosidadData} className="mt-4">
              <RefreshCw className="w-4 h-4 mr-2" />
              Reintentar
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );

};

export default MorosidadAE;
